import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Plus, Trash2, Pencil, X, Save, Search, Loader2, MapPin, Users, CheckSquare, Square, Radar } from "lucide-react";
import { toast } from "sonner";
import { useSettings } from "../../lib/useSettings";

export function FAQAdmin() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = async () => setItems((await api.get("/faqs")).data);
  useEffect(()=>{load();}, []);
  const save = async () => {
    try {
      if (edit.id) await api.put(`/faqs/${edit.id}`, edit); else await api.post("/faqs", edit);
      toast.success("Enregistré"); setEdit(null); load();
    } catch { toast.error("Erreur"); }
  };
  return (
    <div className="p-6 md:p-10" data-testid="admin-faq-page">
      <header className="flex items-center justify-between mb-8">
        <div><div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">SEO longue traîne</div><h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">FAQ</h1></div>
        <Button onClick={()=>setEdit({question:"",answer:"",category:"general",sort_order:0,linked_service:"",linked_zone:""})} className="bg-[color:var(--brand-red)]"><Plus className="w-4 h-4 mr-1"/>Nouvelle</Button>
      </header>
      <div className="space-y-2">
        {items.map(f => (
          <Card key={f.id} className="p-4 border">
            <div className="flex justify-between items-start gap-3">
              <div>
                <div className="text-xs text-[color:var(--brand-sky)] font-bold uppercase">{f.category}</div>
                <div className="font-display font-semibold mt-1">{f.question}</div>
                <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1 line-clamp-2">{f.answer}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="outline" size="sm" onClick={()=>setEdit(f)}><Pencil className="w-3 h-3"/></Button>
                <Button variant="outline" size="sm" onClick={async()=>{await api.delete(`/faqs/${f.id}`); load();}}><Trash2 className="w-3 h-3"/></Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
      {edit && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={()=>setEdit(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b"><h3 className="font-display font-bold text-xl">FAQ</h3><button onClick={()=>setEdit(null)}><X className="w-5 h-5"/></button></div>
            <div className="p-5 space-y-3">
              <div><Label>Question</Label><Input value={edit.question} onChange={e=>setEdit({...edit, question: e.target.value})}/></div>
              <div><Label>Réponse</Label><Textarea rows={5} value={edit.answer} onChange={e=>setEdit({...edit, answer: e.target.value})}/></div>
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>Catégorie</Label><Input value={edit.category} onChange={e=>setEdit({...edit, category: e.target.value})}/></div>
                <div><Label>Ordre</Label><Input type="number" value={edit.sort_order} onChange={e=>setEdit({...edit, sort_order: +e.target.value})}/></div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t"><Button variant="outline" onClick={()=>setEdit(null)}>Annuler</Button><Button onClick={save} className="bg-[color:var(--brand-navy)]"><Save className="w-4 h-4 mr-1"/>Enregistrer</Button></div>
          </div>
        </div>
      )}
    </div>
  );
}

export function ZonesAdmin() {
  const { settings } = useSettings();
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [lookupOpen, setLookupOpen] = useState(false);
  const [lookupCode, setLookupCode] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResults, setLookupResults] = useState([]);
  const [lookupSelected, setLookupSelected] = useState(new Set());
  const [lookupImporting, setLookupImporting] = useState(false);

  // Radius lookup (smart)
  const [radiusOpen, setRadiusOpen] = useState(false);
  const [radiusCity, setRadiusCity] = useState("");
  const [radiusKm, setRadiusKm] = useState(30);
  const [radiusLoading, setRadiusLoading] = useState(false);
  const [radiusResults, setRadiusResults] = useState([]);
  const [radiusMainInfo, setRadiusMainInfo] = useState(null);
  const [radiusSelected, setRadiusSelected] = useState(new Set());
  const [radiusImporting, setRadiusImporting] = useState(false);

  const load = async () => setItems((await api.get("/zones")).data);
  useEffect(()=>{load();}, []);
  const save = async () => {
    try {
      if (edit.id) await api.put(`/zones/${edit.id}`, edit); else await api.post("/zones", edit);
      toast.success("Enregistré"); setEdit(null); load();
    } catch { toast.error("Erreur"); }
  };

  const doLookup = async (e) => {
    if (e) e.preventDefault();
    if (!lookupCode.trim()) return;
    setLookupLoading(true); setLookupResults([]); setLookupSelected(new Set());
    try {
      const { data } = await api.get(`/geo/postal-lookup?code=${encodeURIComponent(lookupCode.trim())}`);
      setLookupResults(data.communes || []);
      // Pré-sélectionner tout
      setLookupSelected(new Set(data.communes.map(c => c.city)));
      if (!data.communes.length) toast.info("Aucune commune trouvée pour ce code.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur de recherche");
    } finally { setLookupLoading(false); }
  };

  const toggleSel = (city) => {
    const nx = new Set(lookupSelected);
    nx.has(city) ? nx.delete(city) : nx.add(city);
    setLookupSelected(nx);
  };

  const importSelected = async () => {
    const zones = lookupResults.filter(c => lookupSelected.has(c.city));
    if (!zones.length) return;
    setLookupImporting(true);
    try {
      const { data } = await api.post("/zones/bulk-create", { zones });
      toast.success(`${data.created.length} zone(s) créée(s), ${data.skipped.length} ignorée(s).`);
      setLookupOpen(false);
      setLookupCode(""); setLookupResults([]); setLookupSelected(new Set());
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'import");
    } finally { setLookupImporting(false); }
  };

  // --- Smart radius lookup ---
  const openRadius = () => {
    // Pre-fill with main_city from settings
    setRadiusCity(settings?.main_city && settings.main_city !== "[Votre ville]" ? settings.main_city : "");
    setRadiusKm(settings?.intervention_radius_km || 30);
    setRadiusResults([]); setRadiusSelected(new Set()); setRadiusMainInfo(null);
    setRadiusOpen(true);
  };

  const doRadiusLookup = async (e) => {
    if (e) e.preventDefault();
    const q = (radiusCity || "").trim();
    if (!q) { toast.error("Entrez une ville principale"); return; }
    setRadiusLoading(true); setRadiusResults([]); setRadiusSelected(new Set());
    try {
      // detect if user typed a postal code (5 digits)
      const isCp = /^\d{5}$/.test(q);
      const param = isCp ? `postal_code=${q}` : `city=${encodeURIComponent(q)}`;
      const { data } = await api.get(`/geo/radius-lookup?${param}&radius_km=${radiusKm}`);
      setRadiusResults(data.communes || []);
      setRadiusMainInfo({ city: data.main_city, postal_code: data.main_postal_code, depts: data.departments_searched || [] });
      // Sélection par défaut : toutes les communes
      setRadiusSelected(new Set(data.communes.map(c => c.city)));
      if (!data.communes.length) toast.info("Aucune commune trouvée dans ce rayon.");
      else toast.success(`${data.count} communes trouvées dans ${data.radius_km} km (${(data.departments_searched || []).length} dpt).`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Recherche impossible");
    } finally { setRadiusLoading(false); }
  };

  const toggleRadiusSel = (city) => {
    const nx = new Set(radiusSelected);
    nx.has(city) ? nx.delete(city) : nx.add(city);
    setRadiusSelected(nx);
  };

  const importRadius = async () => {
    const zones = radiusResults.filter(c => radiusSelected.has(c.city));
    if (!zones.length) return;
    setRadiusImporting(true);
    try {
      const { data } = await api.post("/zones/bulk-create", { zones });
      toast.success(`${data.created.length} zone(s) créée(s), ${data.skipped.length} ignorée(s).`);
      setRadiusOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'import");
    } finally { setRadiusImporting(false); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-zones-page">
      <header className="flex items-center justify-between mb-8">
        <div><div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">SEO local</div><h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Zones desservies</h1></div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={openRadius} variant="outline" className="border-[color:var(--brand-sky)]/40 text-[color:var(--brand-sky)] hover:bg-[color:var(--brand-sky)]/10" data-testid="zones-radius-lookup-btn">
            <Radar className="w-4 h-4 mr-1.5" />Rayon smart
          </Button>
          <Button onClick={() => setLookupOpen(true)} variant="outline" data-testid="zones-postal-lookup-btn">
            <Search className="w-4 h-4 mr-1.5" />Code postal
          </Button>
          <Button onClick={()=>setEdit({city:"",postal_code:"",slug:"",meta_title:"",meta_description:"",content:"",featured:false})} className="bg-[color:var(--brand-red)]"><Plus className="w-4 h-4 mr-1"/>Nouvelle zone</Button>
        </div>
      </header>
      <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map(z => (
          <Card key={z.id} className="p-4 border">
            <div className="font-mono text-xs text-[hsl(var(--muted-foreground))]">{z.postal_code}{z.department ? ` · Dpt ${z.department}` : ""}</div>
            <div className="font-display font-bold text-[color:var(--brand-navy)]">{z.city}</div>
            {z.population > 0 && (
              <div className="text-[10px] text-[hsl(var(--muted-foreground))] flex items-center gap-1 mt-0.5">
                <Users className="w-2.5 h-2.5" />{z.population.toLocaleString("fr-FR")} hab.
              </div>
            )}
            <div className="flex gap-1 mt-2">
              <Button variant="outline" size="sm" onClick={()=>setEdit(z)}><Pencil className="w-3 h-3"/></Button>
              <Button variant="outline" size="sm" onClick={async()=>{await api.delete(`/zones/${z.id}`); load();}}><Trash2 className="w-3 h-3"/></Button>
            </div>
          </Card>
        ))}
        {!items.length && (
          <Card className="p-8 border col-span-full text-center text-sm text-[hsl(var(--muted-foreground))]">
            Aucune zone. Utilisez <strong>Rechercher par code postal</strong> pour en importer rapidement.
          </Card>
        )}
      </div>

      {/* --- MODAL édition zone --- */}
      {edit && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={()=>setEdit(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b"><h3 className="font-display font-bold text-xl">Zone</h3><button onClick={()=>setEdit(null)}><X className="w-5 h-5"/></button></div>
            <div className="p-5 space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>Ville *</Label><Input value={edit.city} onChange={e=>setEdit({...edit, city: e.target.value})}/></div>
                <div><Label>Code postal *</Label><Input value={edit.postal_code} onChange={e=>setEdit({...edit, postal_code: e.target.value})}/></div>
              </div>
              <div><Label>Meta title</Label><Input value={edit.meta_title} onChange={e=>setEdit({...edit, meta_title: e.target.value})}/></div>
              <div><Label>Meta description</Label><Textarea rows={2} value={edit.meta_description} onChange={e=>setEdit({...edit, meta_description: e.target.value})}/></div>
              <div><Label>Contenu SEO local</Label><Textarea rows={5} value={edit.content} onChange={e=>setEdit({...edit, content: e.target.value})}/></div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t"><Button variant="outline" onClick={()=>setEdit(null)}>Annuler</Button><Button onClick={save} className="bg-[color:var(--brand-navy)]"><Save className="w-4 h-4 mr-1"/>Enregistrer</Button></div>
          </div>
        </div>
      )}

      {/* --- MODAL lookup code postal --- */}
      {lookupOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !lookupImporting && setLookupOpen(false)} data-testid="postal-lookup-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b">
              <div>
                <h3 className="font-display font-bold text-xl text-[color:var(--brand-navy)]">Rechercher par code postal</h3>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Via <span className="font-mono">geo.api.gouv.fr</span> — données officielles INSEE.</p>
              </div>
              <button onClick={() => setLookupOpen(false)} disabled={lookupImporting}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 border-b">
              <form onSubmit={doLookup} className="flex gap-2">
                <Input
                  value={lookupCode}
                  onChange={e => setLookupCode(e.target.value.replace(/\D/g, "").slice(0, 5))}
                  placeholder="ex : 13300, 75001, 69002..."
                  inputMode="numeric"
                  maxLength={5}
                  autoFocus
                  className="font-mono text-center tracking-widest text-lg"
                  data-testid="postal-lookup-input"
                />
                <Button type="submit" disabled={lookupLoading || lookupCode.length < 4} className="bg-[color:var(--brand-navy)]" data-testid="postal-lookup-search">
                  {lookupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1.5" />Rechercher</>}
                </Button>
              </form>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {lookupResults.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                      {lookupResults.length} commune{lookupResults.length > 1 ? "s" : ""} trouvée{lookupResults.length > 1 ? "s" : ""}
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setLookupSelected(new Set(lookupResults.map(c => c.city)))}
                        className="text-xs font-semibold text-[color:var(--brand-sky)] hover:underline"
                      >
                        Tout sélectionner
                      </button>
                      <span className="text-[hsl(var(--muted-foreground))]">·</span>
                      <button
                        type="button"
                        onClick={() => setLookupSelected(new Set())}
                        className="text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:underline"
                      >
                        Aucun
                      </button>
                    </div>
                  </div>
                  {lookupResults.map(c => (
                    <label
                      key={c.city}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        lookupSelected.has(c.city)
                          ? "bg-[color:var(--brand-sky)]/10 border-[color:var(--brand-sky)]/40"
                          : "border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/30"
                      }`}
                      data-testid={`postal-result-${c.city}`}
                    >
                      {lookupSelected.has(c.city)
                        ? <CheckSquare className="w-4 h-4 text-[color:var(--brand-sky)] shrink-0" />
                        : <Square className="w-4 h-4 text-[hsl(var(--muted-foreground))] shrink-0" />}
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={lookupSelected.has(c.city)}
                        onChange={() => toggleSel(c.city)}
                      />
                      <MapPin className="w-4 h-4 text-[color:var(--brand-sky)] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-[color:var(--brand-navy)]">{c.city}</div>
                        <div className="text-[11px] text-[hsl(var(--muted-foreground))] flex items-center gap-2">
                          <span className="font-mono">{c.postal_code}</span>
                          <span>Dpt {c.department}</span>
                          {c.population > 0 && (
                            <span className="flex items-center gap-0.5">
                              <Users className="w-2.5 h-2.5" />{c.population.toLocaleString("fr-FR")} hab.
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              {!lookupResults.length && !lookupLoading && (
                <div className="text-center text-sm text-[hsl(var(--muted-foreground))] py-8">
                  Entrez un code postal (4-5 chiffres) pour obtenir la liste des communes associées.
                </div>
              )}
              {lookupLoading && (
                <div className="flex items-center justify-center py-10 text-sm text-[hsl(var(--muted-foreground))]">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />Recherche en cours…
                </div>
              )}
            </div>

            {lookupResults.length > 0 && (
              <div className="p-5 border-t flex items-center justify-between gap-2 bg-[hsl(var(--muted))]/20">
                <div className="text-sm">
                  <strong className="text-[color:var(--brand-navy)]">{lookupSelected.size}</strong>
                  <span className="text-[hsl(var(--muted-foreground))]"> à importer sur {lookupResults.length}</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setLookupOpen(false)} disabled={lookupImporting}>Annuler</Button>
                  <Button
                    onClick={importSelected}
                    disabled={!lookupSelected.size || lookupImporting}
                    className="bg-[color:var(--brand-red)] hover:bg-[color:var(--brand-red)]/90"
                    data-testid="postal-lookup-import"
                  >
                    {lookupImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1.5" />Importer {lookupSelected.size}</>}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- MODAL rayon smart --- */}
      {radiusOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !radiusImporting && setRadiusOpen(false)} data-testid="radius-lookup-modal">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[color:var(--brand-sky)]/10 flex items-center justify-center">
                  <Radar className="w-5 h-5 text-[color:var(--brand-sky)]" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl text-[color:var(--brand-navy)]">Auto-remplissage par rayon</h3>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                    Importez toutes les communes autour d'un point donné — données officielles <span className="font-mono">geo.api.gouv.fr</span>.
                  </p>
                </div>
              </div>
              <button onClick={() => setRadiusOpen(false)} disabled={radiusImporting}><X className="w-5 h-5" /></button>
            </div>

            <div className="p-5 border-b bg-[hsl(var(--muted))]/10">
              <form onSubmit={doRadiusLookup} className="grid md:grid-cols-[1fr_auto_auto] gap-3 items-end">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">Ville ou code postal de référence</Label>
                  <Input
                    value={radiusCity}
                    onChange={e => setRadiusCity(e.target.value)}
                    placeholder="ex : Lyon  ou  13300"
                    autoFocus
                    className="mt-1 h-11"
                    data-testid="radius-city-input"
                  />
                </div>
                <div className="w-32">
                  <Label className="text-xs font-semibold uppercase tracking-wider">Rayon (km)</Label>
                  <div className="mt-1 flex items-center gap-1">
                    <Input
                      type="number"
                      min={1} max={100}
                      value={radiusKm}
                      onChange={e => setRadiusKm(Math.max(1, Math.min(100, Number(e.target.value) || 0)))}
                      className="h-11 text-center font-mono text-lg"
                      data-testid="radius-km-input"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={radiusLoading || !radiusCity.trim()} className="h-11 bg-[color:var(--brand-sky)] hover:bg-[color:var(--brand-sky)]/90" data-testid="radius-search-btn">
                  {radiusLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Radar className="w-4 h-4 mr-1.5" />Scanner</>}
                </Button>
              </form>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {[10, 20, 30, 50, 75].map(km => (
                  <button
                    key={km}
                    type="button"
                    onClick={() => setRadiusKm(km)}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                      radiusKm === km ? "bg-[color:var(--brand-navy)] text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/60"
                    }`}
                  >
                    {km} km
                  </button>
                ))}
                <span className="text-[10px] text-[hsl(var(--muted-foreground))] self-center ml-1">
                  {settings?.main_city && settings.main_city !== "[Votre ville]" ? `Pré-rempli depuis Paramètres : ${settings.main_city}` : "Saisissez ville/CP"}
                </span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {radiusMainInfo && !radiusLoading && (
                <div className="mb-3 p-3 rounded-lg bg-[color:var(--brand-navy)]/5 border border-[color:var(--brand-navy)]/10 flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-[color:var(--brand-navy)]" />
                  <div className="text-sm">
                    Centre : <strong className="text-[color:var(--brand-navy)]">{radiusMainInfo.city}</strong>
                    <span className="font-mono text-xs text-[hsl(var(--muted-foreground))] ml-2">{radiusMainInfo.postal_code}</span>
                    <span className="text-xs text-[hsl(var(--muted-foreground))] ml-3">
                      · {radiusResults.length} communes trouvées dans {(radiusMainInfo.depts || []).length} département{(radiusMainInfo.depts || []).length > 1 ? "s" : ""} ({(radiusMainInfo.depts || []).join(", ")})
                    </span>
                  </div>
                </div>
              )}
              {radiusResults.length > 0 && (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                      {radiusSelected.size}/{radiusResults.length} sélectionnée{radiusSelected.size > 1 ? "s" : ""}
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setRadiusSelected(new Set(radiusResults.map(c => c.city)))} className="text-xs font-semibold text-[color:var(--brand-sky)] hover:underline">Tout</button>
                      <span className="text-[hsl(var(--muted-foreground))]">·</span>
                      <button type="button" onClick={() => setRadiusSelected(new Set(radiusResults.filter(c => c.population >= 1000).map(c => c.city)))} className="text-xs font-semibold text-[color:var(--brand-sky)] hover:underline" title="≥1000 hab">Villes ≥1k hab</button>
                      <span className="text-[hsl(var(--muted-foreground))]">·</span>
                      <button type="button" onClick={() => setRadiusSelected(new Set())} className="text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:underline">Aucun</button>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-1.5">
                    {radiusResults.map(c => (
                      <label
                        key={`${c.city}-${c.postal_code}`}
                        className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                          radiusSelected.has(c.city)
                            ? "bg-[color:var(--brand-sky)]/10 border-[color:var(--brand-sky)]/40"
                            : "border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/30"
                        } ${c.is_main ? "ring-1 ring-[color:var(--brand-red)]/40" : ""}`}
                        data-testid={`radius-result-${c.city}`}
                      >
                        {radiusSelected.has(c.city)
                          ? <CheckSquare className="w-4 h-4 text-[color:var(--brand-sky)] shrink-0" />
                          : <Square className="w-4 h-4 text-[hsl(var(--muted-foreground))] shrink-0" />}
                        <input type="checkbox" className="sr-only" checked={radiusSelected.has(c.city)} onChange={() => toggleRadiusSel(c.city)} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-display font-bold text-[color:var(--brand-navy)] text-sm truncate">{c.city}</span>
                            {c.is_main && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[color:var(--brand-red)]/10 text-[color:var(--brand-red)]">centre</span>}
                          </div>
                          <div className="text-[10px] text-[hsl(var(--muted-foreground))] flex items-center gap-2 font-mono">
                            <span>{c.postal_code}</span>
                            <span>· {c.distance_km} km</span>
                            {c.population > 0 && <span>· {c.population.toLocaleString("fr-FR")} h.</span>}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </>
              )}
              {!radiusResults.length && !radiusLoading && (
                <div className="text-center text-sm text-[hsl(var(--muted-foreground))] py-10">
                  <Radar className="w-12 h-12 mx-auto mb-3 text-[color:var(--brand-sky)]/30" />
                  Saisissez une ville (ou code postal) et un rayon, puis cliquez sur <strong className="text-[color:var(--brand-navy)]">Scanner</strong>.
                </div>
              )}
              {radiusLoading && (
                <div className="flex items-center justify-center py-10 text-sm text-[hsl(var(--muted-foreground))]">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />Scan des départements en cours…
                </div>
              )}
            </div>

            {radiusResults.length > 0 && (
              <div className="p-5 border-t flex items-center justify-between gap-2 bg-[hsl(var(--muted))]/20">
                <div className="text-sm">
                  <strong className="text-[color:var(--brand-navy)]">{radiusSelected.size}</strong>
                  <span className="text-[hsl(var(--muted-foreground))]"> à importer sur {radiusResults.length}</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setRadiusOpen(false)} disabled={radiusImporting}>Annuler</Button>
                  <Button
                    onClick={importRadius}
                    disabled={!radiusSelected.size || radiusImporting}
                    className="bg-[color:var(--brand-red)] hover:bg-[color:var(--brand-red)]/90"
                    data-testid="radius-import-btn"
                  >
                    {radiusImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1.5" />Importer {radiusSelected.size}</>}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
